/**
 * API Service for Certificate Search
 */

import type { SearchResults, TranslationMap, AppUrls } from '../types';

export class ApiService {
    private appUrls: AppUrls;

    constructor(appUrls: AppUrls) {
        this.appUrls = appUrls;
    }

    /**
     * Load translations from TYPO3 endpoint
     */
    async loadTranslations(): Promise<{ translations: TranslationMap; widgetArea: string }> {
        try {
            const response = await fetch(this.appUrls.app_local_lang);

            if (!response.ok) {
                throw new Error(`Translation request failed: ${response.status}`);
            }

            const data = await response.json();

            return {
                translations: data.LocalLang || {},
                widgetArea: data.Widget || '',
            };
        } catch (error) {
            // Failed to load translations
            throw new Error(`Translation loading failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
        }
    }

    /**
     * Search certificates by certificate number
     */
    async searchByCertificateNumber(certificateNumber: string): Promise<SearchResults> {
        const params = `certnr=${encodeURIComponent(certificateNumber)}`;
        return this.performSearch(params);
    }

    /**
     * Search a Czech NDT person certificate by certificate number and person name.
     * Mirrors the former v9 extension tuev_nord_qwcertificatesearch.
     * The searchMode is sent explicitly because the AJAX endpoint runs through a typeNum PAGE object
     * that has no tt_content / FlexForm context, so the controller cannot read it from $this->settings.
     */
    async searchByPerson(certificateNumber: string, personName: string): Promise<SearchResults> {
        const issuer = (window.afc ?? '').toString().trim();
        const country = (window.locked_country ?? '').toString().trim();
        const issuerParam = issuer.length > 0 ? `&appForCompany=${encodeURIComponent(issuer)}` : '';
        const countryParam = country.length > 0 ? `&lockedCountry=${encodeURIComponent(country)}` : '';
        const params = `searchMode=czechNdt${issuerParam}${countryParam}&certnr=${encodeURIComponent(certificateNumber.trim())}&person=${encodeURIComponent(personName.trim())}`;
        return this.performSearch(params);
    }

    /**
     * Search certificates by advanced criteria
     */
    async searchByAdvancedCriteria(company: string, norm: string, city?: string, country?: string): Promise<SearchResults> {
        // Send raw strings without encoding spaces (legacy API compatibility)
        let params = `company=${company}&norm=${norm}`;

        if (city && city.trim().length > 0) {
            params += `&city=${city.trim()}`;
        }

        if (country && country.trim().length > 0) {
            params += `&country=${country.trim()}`;
        }

        return this.performSearch(params);
    }

    /**
     * Perform the actual search request
     */
    private async performSearch(params: string): Promise<SearchResults> {
        try {
            const response = await fetch(this.appUrls.app_search_url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded',
                    'Accept': 'application/json',
                },
                body: params,
            });

            if (!response.ok) {
                throw new Error(`Search request failed: ${response.status}`);
            }

            const results = await response.json();

            // Normalize API response to internal format
            if (results.CertData) {
                results.list = results.CertData; // Add legacy property for compatibility
            }

            return results;
        } catch (error) {
            // Certificate search error

            // Return structured error response
            return {
                error: error instanceof Error ? error.message : 'Unknown search error',
                list: [],
            };
        }
    }

    /**
     * Validate that required URLs are available
     */
    static validateAppUrls(appUrls: any): appUrls is AppUrls {
        if (!appUrls || typeof appUrls !== 'object') {
            return false;
        }

        const required = ['app_local_lang', 'app_search_url'];
        return required.every((key) => typeof appUrls[key] === 'string' && appUrls[key].length > 0);
    }
}
